
import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRoles } from '@/hooks/use-user-roles';
import { LoadingFallback } from '../common/LoadingFallback';
import { supabase } from '@/integrations/supabase/client';
import { getLogger } from '@/utils/logger';

const log = getLogger({ module: 'AdminRoute' });

export const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, refreshSession } = useAuth();
  const { isAdmin, loading } = useUserRoles();
  
  useEffect(() => {
    // Validate admin session on component mount
    const validateAdminSession = async () => {
      if (user && isAdmin) {
        try {
          const { data } = await supabase.auth.getSession();
          if (!data.session) {
            log.warn('AdminRoute - Admin session lost, refreshing');
            await refreshSession();
          }
        } catch (err) {
          log.error('AdminRoute - Error validating admin session:', err);
        }
      }
    };
    
    validateAdminSession();
  }, [user, isAdmin, refreshSession]);

  log.info('AdminRoute - State:', {
    user: user ? { id: user.id } : null,
    isAdmin,
    loading
  });

  if (loading) {
    log.info('AdminRoute - Waiting for role data...');
    return <LoadingFallback />;
  }

  if (!isAdmin) {
    log.info('AdminRoute - User is not an admin, redirecting...');
    return <Navigate to="/bulletins" replace />;
  }

  log.info('AdminRoute - User is admin, rendering protected content');
  return <>{children}</>;
};
